import {
  BookOpen,
  Calculator,
  Calendar,
  CircleDollarSign,
  Layers,
  LineChart,
  MapPin,
  Users,
  Wallet,
  Zap,
} from "lucide-react";
import type { HelpSection } from "./types";

export const HELP_SECTIONS: HelpSection[] = [
  {
    id: "overview",
    title: "Overview",
    description: "How BurnChart models your startup finances.",
    icon: BookOpen,
    blocks: [
      {
        type: "paragraph",
        text: "BurnChart builds a month-by-month cash timeline from your inputs: expenses, revenues, timeline events, and optional customer projections. Every chart, runway number, and funding scenario uses the same underlying calculation engine so numbers stay consistent across the app.",
      },
      {
        type: "heading",
        text: "Core concepts",
      },
      {
        type: "table",
        headers: ["Concept", "What it models"],
        rows: [
          ["Revenue plan", "How you charge each customer (subscription, per visit, or hybrid)."],
          ["Customer projection", "How many customers you acquire, retain, and serve over time."],
          ["Capacity group", "A shared physical limit (office, chairs, providers) across projections."],
          ["Expense", "Money going out — fixed, scaling, one-time, or customer-based."],
          ["Revenue line", "Non-customer income (grants, services) or manual sales."],
          ["Event", "One-time cash or capacity changes (funding, assets, milestones)."],
        ],
      },
      {
        type: "callout",
        variant: "tip",
        title: "Recommended workflow",
        text: "1) Set startup dates and initial funding in Settings. 2) Add revenue plans (pricing). 3) Add customer projections (growth). 4) Add operating expenses. 5) Add funding and milestone events. 6) Review the timeline and funding calculator.",
      },
    ],
  },
  {
    id: "timeline-ui",
    title: "Timeline & dashboard",
    description: "Navigating charts, quick actions, and views.",
    icon: LineChart,
    blocks: [
      {
        type: "paragraph",
        text: "The dashboard is your financial command center. The burndown chart shows cumulative cash over time. Supporting charts break out revenue, expenses, customer growth, runway projections, and cash-flow health.",
      },
      {
        type: "heading",
        text: "Quick actions",
      },
      {
        type: "list",
        items: [
          "Expense — recurring or one-time operating cost.",
          "Revenue — manual revenue line (separate from customer-driven revenue).",
          "Event — funding round, launch, asset purchase, or milestone.",
          "Customer projection — model customer growth and linked pricing.",
          "Revenue plan — define subscription, per-visit, or hybrid pricing.",
          "Capacity group — shared location or resource limit.",
          "Funding calculator — runway-based raise scenarios.",
          "AI chat — ask questions using your live financial data.",
        ],
      },
      {
        type: "heading",
        text: "Timeline controls",
      },
      {
        type: "list",
        items: [
          "Zoom — switch between month, quarter, and year views.",
          "Pan — move the visible date range along the timeline.",
          "Edit view — tabular management of all financial records.",
          "Bulk date shift — move many expenses/revenues/events at once.",
        ],
      },
      {
        type: "callout",
        variant: "info",
        text: "Hover or tap chart points for month-level detail. Customer-driven revenue and acquisition costs appear automatically when customer projections are configured — you do not need duplicate manual lines for those unless you want overrides.",
      },
    ],
  },
  {
    id: "revenue-plans",
    title: "Revenue plans",
    description: "Pricing models — not customer counts.",
    icon: CircleDollarSign,
    blocks: [
      {
        type: "paragraph",
        text: "A revenue plan answers: “How much does one customer pay?” It does not track how many customers you have. Customer counts live in customer projections, which link to a revenue plan for pricing.",
      },
      {
        type: "heading",
        text: "Revenue models",
      },
      {
        type: "table",
        headers: ["Model", "Revenue formula (per customer / month)"],
        rows: [
          ["Subscription", "Monthly price (quarterly and yearly prices are converted to a monthly equivalent)."],
          ["Per visit", "Price per visit × average visits per customer per month × seasonality multiplier."],
          ["Hybrid", "Subscription monthly price + visit revenue (both components)."],
        ],
      },
      {
        type: "formula",
        label: "Visit seasonality",
        expression: "Effective visits = avg visits × seasonality[month]",
        note: "Seasonality is set per calendar quarter (Q1–Q4). A value of 1.2 means 20% more visits than baseline that quarter.",
      },
      {
        type: "callout",
        variant: "info",
        title: "Not tied to capacity",
        text: "Revenue plans have no capacity limit. A single plan can be used by many customer projections. Capacity is configured on projections, not on pricing.",
      },
    ],
  },
  {
    id: "customer-projections",
    title: "Customer projections",
    description: "Growth, churn, costs, and pricing links.",
    icon: Users,
    blocks: [
      {
        type: "paragraph",
        text: "A customer projection models one customer segment over time: how you acquire customers, how many churn, what you pay to acquire and serve them, and which revenue plan prices them.",
      },
      {
        type: "heading",
        text: "Projection types",
      },
      {
        type: "list",
        items: [
          "Fixed monthly acquisition — add a set number of new customers each month (good for cold start).",
          "Growth + acquisition — percentage growth on existing base plus optional fixed new customers per month.",
          "Custom timeline — specify expected customer counts at specific months.",
        ],
      },
      {
        type: "heading",
        text: "Month-by-month simulation",
      },
      {
        type: "list",
        ordered: true,
        items: [
          "Start from initial customers at the projection start date.",
          "Each month: apply churn to existing customers.",
          "Add planned acquisitions (respecting caps and shared capacity).",
          "Calculate revenue from active customers × revenue plan pricing.",
          "Calculate acquisition cost from new customers × cost per customer.",
          "Calculate servicing cost from active customers × monthly servicing cost.",
        ],
      },
      {
        type: "formula",
        label: "Steady state (fixed acquisition, no growth)",
        expression: "Equilibrium customers ≈ monthly acquisitions ÷ monthly churn rate",
        note: "Shown as a capacity insight when churn is greater than zero. With 0% churn and ongoing growth, there is no finite steady state — set a cap for realism.",
      },
      {
        type: "heading",
        text: "Form tabs",
      },
      {
        type: "table",
        headers: ["Tab", "Fields"],
        rows: [
          ["Basics", "Name, description, revenue plan link, start/end dates."],
          ["Growth", "Projection type, acquisition/growth fields, churn."],
          ["Costs", "Acquisition cost per customer, servicing cost, channel."],
          ["Capacity", "Per-projection cap, monthly onboarding cap, shared capacity group."],
        ],
      },
      {
        type: "callout",
        variant: "tip",
        text: "Revenue plan = pricing. Customer projection = how many customers and how they grow. Link them on the Basics tab; configure shared location limits on the Capacity tab.",
      },
    ],
  },
  {
    id: "capacity-groups",
    title: "Capacity groups",
    description: "Shared limits across projections and locations.",
    icon: MapPin,
    blocks: [
      {
        type: "paragraph",
        text: "A capacity group represents a shared limit — typically a physical location, provider roster, or operational ceiling. Multiple customer projections can point to the same group; their active customers are summed and cannot exceed the group maximum.",
      },
      {
        type: "callout",
        variant: "info",
        title: "Example: Office Location 1 (max 200)",
        text: "Create capacity group “Office Location 1” with max 200. Create projection “Insurance patients” and projection “Cash patients”, both linked to that group. Even if each projection would grow to 150 alone, combined they stop at 200.",
      },
      {
        type: "heading",
        text: "Capacity group vs projection cap",
      },
      {
        type: "table",
        headers: ["Setting", "Scope"],
        rows: [
          ["Max customers (on projection)", "Limits only that one projection segment."],
          ["Shared capacity group", "Limits the sum of all projections using that group."],
          ["Max new customers / month", "Limits onboarding speed for one projection."],
        ],
      },
      {
        type: "paragraph",
        text: "If both a projection cap and a group are set, the stricter limit applies. For a single stream at one location, you can use only a projection-level max customers (e.g. 200) without creating a group. Use a group when multiple projections share the same location.",
      },
      {
        type: "heading",
        text: "Milestone capacity increases",
      },
      {
        type: "paragraph",
        text: "Milestone events can increase capacity on a date — either for a specific capacity group (new location wing) or a specific projection. The simulator adds the increase to the effective max from that month forward.",
      },
      {
        type: "callout",
        variant: "warn",
        text: "Market saturation from growth scenarios slows acquisition as you approach a market size limit. That is separate from hard capacity caps (office size). Hard caps stop growth; saturation gradually reduces new acquisitions.",
      },
    ],
  },
  {
    id: "expenses-revenues",
    title: "Expenses & revenues",
    description: "Operating lines and scaling behavior.",
    icon: Wallet,
    blocks: [
      {
        type: "heading",
        text: "Expense types",
      },
      {
        type: "table",
        headers: ["Type", "Behavior"],
        rows: [
          ["Fixed", "Constant monthly amount (optional end date)."],
          ["Percentage of revenue", "Scales with total monthly revenue."],
          ["Hybrid", "Base amount plus percentage of revenue."],
          ["Customer-based", "Per-customer cost × active customers from projections."],
          ["One-time", "Single charge in the start month."],
        ],
      },
      {
        type: "heading",
        text: "Projected customer costs",
      },
      {
        type: "paragraph",
        text: "When customer projections are active, acquisition costs and servicing costs are computed automatically and reflected in burn. Manual customer-acquisition expense lines tied to projections are excluded from double-counting.",
      },
      {
        type: "heading",
        text: "Revenue lines",
      },
      {
        type: "list",
        items: [
          "Manual revenue — grants, consulting, or other non-customer income.",
          "Customer-driven flag — links a revenue line to projection tiers (legacy path; prefer customer projections for subscription/visit revenue).",
          "Growth rate — compound monthly growth on recurring revenue.",
        ],
      },
    ],
  },
  {
    id: "events",
    title: "Timeline events",
    description: "Funding, assets, launches, and milestones.",
    icon: Calendar,
    blocks: [
      {
        type: "table",
        headers: ["Event type", "Effect"],
        rows: [
          ["Funding", "Adds cash to the timeline on the event date."],
          ["Asset", "Cash outflow for purchase; ongoing appreciation/depreciation each month."],
          ["Launch / Milestone", "Markers on the timeline; milestones can increase capacity."],
          ["Other", "Custom marker or amount."],
        ],
      },
      {
        type: "formula",
        label: "Asset book value",
        expression: "Monthly change = current value × monthly appreciation rate",
        note: "Rates are entered per month (6 = 6%/month). Value will not fall below minimum/salvage value.",
      },
      {
        type: "paragraph",
        text: "Initial funding configured in startup settings is applied at the plan start date. Additional funding events stack on top at their respective dates.",
      },
      {
        type: "callout",
        variant: "info",
        text: "Asset events can be revised from a date forward when balances or appreciation rates change. Use Revise from a date in the edit form instead of duplicating the asset.",
      },
    ],
  },
  {
    id: "revisions",
    title: "Revisions",
    description: "Split recurring items at a date without rewriting history.",
    icon: Layers,
    blocks: [
      {
        type: "paragraph",
        text: "Revisions let you update forward assumptions for assets, recurring revenue, and recurring expenses while keeping past months exactly as originally modeled. Each logical line item becomes a chain of segments; exactly one segment is active per month.",
      },
      {
        type: "table",
        headers: ["Action", "When to use"],
        rows: [
          ["Edit", "Change name, color, category, or fix the current open segment values."],
          ["Revise from a date", "Split at a month: past stays frozen, new amount/rate/growth applies forward."],
          ["Undo latest revision", "Remove the newest segment and reopen the previous one (grid history panel)."],
        ],
      },
      {
        type: "list",
        items: [
          "Sky ◆ markers on the timeline show where revisions start (toggle with Revisions on/off).",
          "Grid rows with multiple segments expand to show history; use the history icon for details.",
          "Editing amount or rate on an older segment warns you because it rewrites history for later months.",
          "One-time revenue/expense and customer-driven revenue cannot be revised — add a new row or revise the projection instead.",
        ],
      },
    ],
  },
  {
    id: "calculations",
    title: "Core calculations",
    description: "Formulas used across charts and runway.",
    icon: Calculator,
    blocks: [
      {
        type: "formula",
        label: "Monthly burn",
        expression: "Net monthly burn = Total monthly expenses − Total monthly revenue",
        note: "Revenue includes customer subscription/visit revenue from projections plus manual revenue lines.",
      },
      {
        type: "formula",
        label: "Runway",
        expression: "Runway (months) = Available cash ÷ Net monthly burn",
        note: "If net burn ≤ 0 (profitable), runway is treated as unlimited (∞).",
      },
      {
        type: "formula",
        label: "Cumulative cash",
        expression: "Cash(month) = Cash(month−1) + Net cash flow(month)",
        note: "Net cash flow includes operating revenue/expenses, funding events, asset purchases, and asset monthly impacts.",
      },
      {
        type: "formula",
        label: "Customer revenue (month)",
        expression: "Σ active customers × revenue plan price (per model)",
      },
      {
        type: "formula",
        label: "Acquisition cost (month)",
        expression: "New customers acquired × acquisition cost per customer",
      },
      {
        type: "formula",
        label: "Servicing cost (month)",
        expression: "Active customers × monthly servicing cost per customer",
      },
      {
        type: "callout",
        variant: "info",
        text: "All months use UTC month boundaries for consistency. The financial cache pre-computes monthly aggregates so timeline panning and scenario views stay fast and aligned.",
      },
    ],
  },
  {
    id: "funding-calculator",
    title: "Funding calculator",
    description: "Raise scenarios and perspectives.",
    icon: Zap,
    blocks: [
      {
        type: "paragraph",
        text: "The funding calculator estimates how much capital you need for target runway lengths. It uses the same burn and revenue figures as the burndown chart at the selected analysis date.",
      },
      {
        type: "heading",
        text: "Perspectives",
      },
      {
        type: "table",
        headers: ["Mode", "Cash assumed", "Use case"],
        rows: [
          ["Current position", "Your actual cash on the timeline date", "Bridge round or extension from today."],
          ["Initial raise", "$0 bank balance at launch", "Total opening raise to fund operations from day zero."],
        ],
      },
      {
        type: "heading",
        text: "Scenarios",
      },
      {
        type: "list",
        items: [
          "Survival — ~6 months of runway.",
          "Growth — ~15 months (typical seed range).",
          "Series prep — ~24 months to reach next-round metrics.",
        ],
      },
      {
        type: "formula",
        label: "Scenario ask (current position)",
        expression: "Ask = max(0, net burn × months − current cash)",
      },
      {
        type: "formula",
        label: "Scenario ask (initial raise)",
        expression: "Ask = net burn × months",
        note: "Does not subtract existing cash because launch is modeled from $0.",
      },
    ],
  },
  {
    id: "data-model",
    title: "Data model map",
    description: "How entities connect.",
    icon: Layers,
    blocks: [
      {
        type: "paragraph",
        text: "Understanding relationships prevents common setup mistakes.",
      },
      {
        type: "list",
        ordered: true,
        items: [
          "Startup — plan dates, initial funding, currency.",
          "Revenue plan (1) ← used by → Customer projection (many).",
          "Capacity group (1) ← shared by → Customer projections (many).",
          "Customer projection — produces customers, revenue, acquisition & servicing costs.",
          "Expense / Revenue lines — independent operating items.",
          "Events — modify cash or capacity on specific dates.",
        ],
      },
      {
        type: "callout",
        variant: "tip",
        title: "Quick rule",
        text: "Pricing lives on revenue plans. Headcount lives on customer projections. Shared offices live on capacity groups.",
      },
    ],
  },
];

export const HELP_SECTION_MAP = Object.fromEntries(
  HELP_SECTIONS.map((section) => [section.id, section]),
) as Record<string, HelpSection>;

export const DEFAULT_HELP_SECTION_ID = HELP_SECTIONS[0]?.id ?? "overview";
